import React from 'react';
import { Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { SiteCategory, SiteResource, SystemIntel } from '../../types';
import { BlueLootPrice, DamageProfile, ResourcePrice, SiteThreat, fetchSiteDetail } from '../../api/sites';
import { fetchFittingRecommendations } from '../../api/fittings';
import ConsolePanel from '../../components/ConsolePanel';
import EffectStats from '../../components/EffectStats';
import NpcWaveTable from '../../components/NpcWaveTable';
import SuggestedFittings from '../../components/SuggestedFittings';
import LcarsBlock from '../../components/LcarsBlock';
import LcarsRule from '../../components/LcarsRule';
import ScanFrame from '../../components/ScanFrame';
import StatusBadge from '../../components/StatusBadge';
import { C, FONT, classColor, frameSx, glow, hexToRgba, igniteSx, lcarsLabelSx, revealSx } from '../../theme/tokens';

const CATEGORY_ACCENT: Record<string, string> = {
  combat: C.red,
  relic: C.gold,
  data: C.gold,
  gas: C.cyan,
  ore: C.cyan,
  ice: C.cyan,
};

/** The categories you go to with a harvester rather than with guns. */
const HARVEST = new Set<SiteCategory>(['gas', 'ore', 'ice']);

const nf = new Intl.NumberFormat('en-US');

/** ISK reads in magnitudes, not digits — "1.4b" is a decision, "1,398,000,000"
 * is arithmetic. Exact figures stay in the tooltips. */
const isk = (n: number): string => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)}b`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}m`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}k`;
  return nf.format(Math.round(n));
};

/** Magnitudes, not digits. A C5 site's total EHP is seven figures and
 * "1767k" is not a number anyone reads — it has to turn over into millions
 * the way ISK does. Exact figures stay in the tooltips. */
const compact = (n: number): string =>
  n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}m` : n >= 10_000 ? `${Math.round(n / 1000)}k` : nf.format(Math.round(n));

/**
 * The site in one strip, above everything else.
 *
 * This is the answer to "should I warp in", and it has to survive being read
 * in the four seconds someone actually gives it: what hits you at its worst,
 * how hard the spike is, how much you have to shoot, what EWAR breaks your
 * fit, and what it pays. Everything below this strip is the evidence for
 * these numbers — the wave-by-wave table, the field contents, the loot rows.
 *
 * Peak vs. chain: waves are sequential, so `peak` is the wave your tank has
 * to hold against. `allWaves` only matters if you shoot triggers out of
 * order, which is why it is a footnote here rather than the headline —
 * leading with it would tell every pilot a site hits several times harder
 * than the one they are about to run.
 */
const StatBlock: React.FC<{ label: string; value: string; accent: string; hint?: string; sub?: string }> = ({
  label,
  value,
  accent,
  hint,
  sub,
}) => {
  const body = (
    <Box sx={{ minWidth: 0, cursor: hint ? 'help' : 'default' }}>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.6rem') }}>{label}</Typography>
      <Typography
        sx={{
          fontFamily: FONT.mono,
          fontWeight: 700,
          fontSize: { xs: '1.15rem', md: '1.4rem' },
          lineHeight: 1.1,
          color: accent,
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.62rem' }}>{sub}</Typography>
      )}
    </Box>
  );
  return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
};

/** Incoming damage by type, as one stacked bar.
 *
 * A bar rather than four badges because the decision it feeds is
 * proportional — "which hole do I plug first" — and four equal-looking tags
 * hide that a site is 40% EM. Types below 1% are dropped rather than
 * rendered as a hairline nobody can read or hover.
 *
 * Shares are of SUSTAINED damage, weighted by each weapon's own cycle time.
 * Sleepers fire an energy beam (EM + thermal) and a missile (kinetic +
 * explosive) on different intervals, so a per-volley share badly overstates
 * the missile half — see scripts/regen-sleeper-ships.ts.
 *
 * This never argues against an omnitank, which is what the EVE University
 * Sleepers page tells you to fit and what the bar's own footnote repeats.
 * All four holes are real on any site with ships in it; they are just not
 * the same size, and that is the part a blanket four-type tag couldn't say. */
const DAMAGE_COLORS: Record<string, string> = {
  em: C.blue,
  thermal: C.red,
  kinetic: C.neutral,
  explosive: C.gold,
};

const DamageSplit: React.FC<{ profile: DamageProfile }> = ({ profile }) => {
  const parts = Object.entries(profile)
    .filter(([, pct]) => pct >= 1)
    .sort((a, b) => b[1] - a[1]);
  if (parts.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.6rem'), mb: 0.5 }}>incoming damage &middot; sustained</Typography>
      <Stack
        direction="row"
        sx={{
          height: 8,
          borderRadius: 999,
          overflow: 'hidden',
          border: `1px solid ${C.hairline}`,
          // The segments below are percentages of this box's CONTENT width,
          // so without border-box the hairline is added outside them and the
          // bar ends up 2px wider than the panel that holds it — which is
          // exactly how much the document overflowed a 390px phone.
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        {parts.map(([type, pct]) => (
          <Tooltip key={type} title={`${pct}% ${type}`}>
            <Box sx={{ width: `${pct}%`, bgcolor: DAMAGE_COLORS[type] ?? C.neutral, cursor: 'help' }} />
          </Tooltip>
        ))}
      </Stack>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {parts.map(([type, pct]) => (
          <Stack key={type} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: DAMAGE_COLORS[type] ?? C.neutral }} />
            <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim }}>
              {type} <Box component="span" sx={{ color: C.text, fontWeight: 600 }}>{pct}%</Box>
            </Typography>
          </Stack>
        ))}
      </Stack>
      {/* The split says which resist pays off most; it does not say to leave
          a hole. Sleepers land every shot hard enough that a single-type
          tank dies to the other three — so the source's own advice ships
          alongside the numbers rather than being implied away by them. */}
      <Typography sx={{ color: C.textDim, fontSize: '0.72rem', mt: 0.75, lineHeight: 1.45 }}>
        Omnitank — Sleepers fire an energy beam (EM/thermal) and missiles (kinetic/explosive) together, and hit hard
        enough per shot to hurt through high resists. Stack the biggest share first, but never leave a type open.
      </Typography>
    </Box>
  );
};

const SiteBriefing: React.FC<{
  threat: SiteThreat | null;
  accent: string;
  damageTypes?: string[];
  payout: { label: string; value: string; hint: string } | null;
}> = ({ threat, accent, damageTypes, payout }) => {
  if (!threat && !payout) return null;
  const floor = threat && !threat.peak.complete ? '≥' : '';

  return (
    <ConsolePanel title="At a Glance" accent={accent} corners={false} sx={frameSx(accent)}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(auto-fit, minmax(120px, 1fr))' },
          gap: 2,
        }}
      >
        {threat && threat.peak.dps > 0 && (
          <StatBlock
            label="peak dps"
            accent={C.red}
            value={`${floor}${compact(threat.peak.dps)}`}
            sub={threat.peakWave ?? undefined}
            hint={`${nf.format(Math.round(threat.peak.dps))} DPS in the site's heaviest single wave — the moment your tank has to hold. All ${threat.waveCount} waves alive at once (a chain-trigger) would be ${nf.format(Math.round(threat.allWaves.dps))}.`}
          />
        )}
        {threat && threat.peak.volley > 0 && (
          <StatBlock
            label="peak alpha"
            accent={C.gold}
            value={`${floor}${compact(threat.peak.volley)}`}
            hint={`${nf.format(Math.round(threat.peak.volley))} damage if every weapon in that wave lands on the same tick. Buffer, not repair rate, is what absorbs this.`}
          />
        )}
        {threat && threat.allWaves.ehp > 0 && (
          <StatBlock
            label="total ehp"
            accent={C.cyan}
            value={`${floor}${compact(threat.allWaves.ehp)}`}
            sub={`${threat.waveCount} wave${threat.waveCount === 1 ? '' : 's'}`}
            hint={`${nf.format(Math.round(threat.allWaves.ehp))} EHP across every wave — the whole grind, escalations excluded.`}
          />
        )}
        {threat && threat.triggerCount > 0 && (
          <StatBlock
            label="triggers"
            accent={C.red}
            value={String(threat.triggerCount)}
            sub={threat.triggerCount === 1 ? 'wave' : 'waves'}
            hint={`${threat.triggerCount} wave${threat.triggerCount === 1 ? '' : 's'} escalate when the marked ship in them dies. Kill the trigger last unless you want the rest of the site on you at once — the wave table below marks which ship it is.`}
          />
        )}
        {payout && (
          <StatBlock label={payout.label} accent={C.green} value={payout.value} hint={payout.hint} />
        )}
      </Box>

      {/* EWAR first: it is what breaks a fit outright, where damage type
          only shapes which resist you stack. */}
      {(threat?.ewar.length ?? 0) > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {threat!.ewar.map((e) => (
            <StatusBadge key={e} label={e} accent={C.gold} />
          ))}
        </Stack>
      )}

      {/* The real split, not "Sleepers do all four".
          The catalogue tags every combat site with all four damage types,
          which is true of the family and useless for a fit: what actually
          lands is turret EM/thermal plus missile kinetic/explosive, in
          proportions that differ per site with the hulls in it. The blanket
          tags are only kept when there is no derived profile to replace
          them. */}
      {threat?.damageProfile ? (
        <DamageSplit profile={threat.damageProfile} />
      ) : (
        damageTypes &&
        damageTypes.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {damageTypes.map((d) => (
              <StatusBadge key={d} label={d} accent={C.red} />
            ))}
          </Stack>
        )
      )}

      {threat?.escalation && threat.escalation.dps > 0 && (
        <Typography sx={{ color: C.textDim, fontSize: '0.78rem', mt: 1.5 }}>
          Escalation content on top of the above, only if you push the site:{' '}
          {compact(threat.escalation.dps)} DPS and {compact(threat.escalation.ehp)} EHP more.
        </Typography>
      )}
    </ConsolePanel>
  );
};

/**
 * What the site holds, as a table.
 *
 * Share is derived here rather than stored, so it cannot drift from the units
 * beside it — and it is only shown when every row has units, because a
 * percentage computed over a partial total is worse than no percentage. The
 * roids column disappears entirely on gas sites, where there are none.
 */
const ResourceTable: React.FC<{ rows: SiteResource[]; prices: Record<string, ResourcePrice> }> = ({ rows, prices }) => {
  const anyRoids = rows.some((r) => r.roids !== undefined);
  const anyUnits = rows.some((r) => r.units !== undefined);
  const anyPrice = rows.some((r) => prices[r.name]);
  // Field value is only honest where the source gives units — a roid count
  // is not a volume, so multiplying it by an ISK/unit price would invent a
  // number. Ore sites therefore get per-unit prices and no total.
  const anyValue = rows.some((r) => r.units !== undefined && prices[r.name]?.buy);
  // A column only gets a total — and units only get a share — when every row
  // has that number. Summing over rows where some are blank quietly
  // understates the field, which is worse than leaving the cell empty.
  const roidTotal = anyRoids && rows.every((r) => r.roids !== undefined) ? rows.reduce((n, r) => n + (r.roids ?? 0), 0) : 0;
  const unitTotal = anyUnits && rows.every((r) => r.units !== undefined) ? rows.reduce((n, r) => n + (r.units ?? 0), 0) : 0;
  const valueTotal = anyValue
    ? rows.reduce((n, r) => n + (r.units ?? 0) * (prices[r.name]?.buy ?? 0), 0)
    : 0;
  const cols = [
    'minmax(0,1fr)',
    ...(anyRoids ? ['auto'] : []),
    ...(anyUnits ? ['auto'] : []),
    ...(unitTotal > 0 ? ['auto'] : []),
    ...(anyPrice ? ['auto', 'auto'] : []),
    ...(anyValue ? ['auto'] : []),
  ].join(' ');
  const head = [
    'resource',
    ...(anyRoids ? ['roids'] : []),
    ...(anyUnits ? ['units'] : []),
    ...(unitTotal > 0 ? ['share'] : []),
    ...(anyPrice ? ['jita buy', 'jita sell'] : []),
    ...(anyValue ? ['value'] : []),
  ];
  const num = { color: C.textDim, fontFamily: FONT.mono, fontSize: '0.82rem', textAlign: 'right' } as const;
  const foot = { borderTop: `1px solid ${hexToRgba(C.cyan, 0.25)}`, pt: 0.6, mt: 0.35 };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: cols, columnGap: 2, rowGap: 0.5, alignItems: 'baseline' }}>
      {head.map((h, i) => (
        <Typography
          key={h}
          sx={{ ...lcarsLabelSx(C.cyan, '0.62rem'), textAlign: i === 0 ? 'left' : 'right', pb: 0.25 }}
        >
          {h}
        </Typography>
      ))}
      {rows.map((r) => {
        const price = prices[r.name];
        const value = r.units !== undefined && price?.buy ? r.units * price.buy : null;
        return (
          <React.Fragment key={r.name}>
            {/* A renamed variant carries its current in-game name, because
                the catalogue's name is what the site's own info window used
                to say and is no longer findable in the market window. */}
            <Tooltip
              title={
                price?.viaBaseOre
                  ? `Priced as ${price.typeName} — CCP renamed this variant, and it differs from the base ore only in mineral yield.`
                  : ''
              }
            >
              <Typography sx={{ color: C.text, fontSize: '0.85rem', cursor: price?.viaBaseOre ? 'help' : 'default' }}>
                {r.name}
                {price?.viaBaseOre && (
                  <Box component="span" sx={{ color: C.textDim, fontSize: '0.7rem' }}> · as {price.typeName}</Box>
                )}
              </Typography>
            </Tooltip>
            {anyRoids && <Typography sx={num}>{r.roids === undefined ? '—' : nf.format(r.roids)}</Typography>}
            {anyUnits && <Typography sx={num}>{r.units === undefined ? '—' : nf.format(r.units)}</Typography>}
            {unitTotal > 0 && (
              <Typography sx={{ ...num, color: C.cyan }}>{Math.round(((r.units ?? 0) / unitTotal) * 100)}%</Typography>
            )}
            {anyPrice && (
              <>
                <Typography sx={{ ...num, color: C.green }}>{price?.buy ? isk(price.buy) : '—'}</Typography>
                <Typography sx={num}>{price?.sell ? isk(price.sell) : '—'}</Typography>
              </>
            )}
            {anyValue && (
              <Typography sx={{ ...num, color: value ? C.green : C.textDim, fontWeight: 600 }}>
                {value ? isk(value) : '—'}
              </Typography>
            )}
          </React.Fragment>
        );
      })}
      {/* What is actually in the field, which is the number you decide on:
          how many rocks to chew through, or how much gas the hold has to
          take. Neither was on the page before — the rows were, the sum was
          left to the reader. */}
      {rows.length > 1 && (roidTotal > 0 || unitTotal > 0) && (
        <>
          <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.62rem'), ...foot }}>total</Typography>
          {anyRoids && (
            <Typography sx={{ ...num, ...foot, color: C.text }}>{roidTotal > 0 ? nf.format(roidTotal) : '—'}</Typography>
          )}
          {anyUnits && (
            <Typography sx={{ ...num, ...foot, color: C.text }}>{unitTotal > 0 ? nf.format(unitTotal) : '—'}</Typography>
          )}
          {unitTotal > 0 && <Typography sx={{ ...num, ...foot, color: C.cyan }}>100%</Typography>}
          {anyPrice && (
            <>
              <Box sx={foot} />
              <Box sx={foot} />
            </>
          )}
          {anyValue && (
            <Tooltip title={`${nf.format(Math.round(valueTotal))} ISK at current Jita buy, for the whole field`}>
              <Typography sx={{ ...num, ...foot, color: C.green, fontWeight: 700, cursor: 'help' }}>
                {isk(valueTotal)}
              </Typography>
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );
};

/** What the Sleeper components are going for right now.
 *
 * Blue loot has no use beyond being sold, and NPC buy orders for it sit in
 * every station — which is why its Jita buy price barely moves and why "buy"
 * is the honest number to plan a haul against. The site's own blue-loot
 * figure (from the wiki, beside this panel) is the payout; this is what that
 * payout is worth today. */
const BlueLootPanel: React.FC<{ prices: BlueLootPrice[] }> = ({ prices }) => {
  const num = { color: C.textDim, fontFamily: FONT.mono, fontSize: '0.82rem', textAlign: 'right' } as const;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto', columnGap: 2, rowGap: 0.5, alignItems: 'baseline' }}>
      {['component', 'jita buy', 'jita sell'].map((h, i) => (
        <Typography key={h} sx={{ ...lcarsLabelSx(C.gold, '0.62rem'), textAlign: i === 0 ? 'left' : 'right', pb: 0.25 }}>
          {h}
        </Typography>
      ))}
      {prices.map((p) => (
        <React.Fragment key={p.typeId}>
          <Typography sx={{ color: C.text, fontSize: '0.85rem' }}>{p.name}</Typography>
          <Tooltip title={p.buy ? `${nf.format(p.buy)} ISK each` : 'no buy order at Jita 4-4'}>
            <Typography sx={{ ...num, color: C.green, cursor: 'help' }}>{p.buy ? isk(p.buy) : '—'}</Typography>
          </Tooltip>
          <Tooltip title={p.sell ? `${nf.format(p.sell)} ISK each` : 'no sell order at Jita 4-4'}>
            <Typography sx={{ ...num, cursor: 'help' }}>{p.sell ? isk(p.sell) : '—'}</Typography>
          </Tooltip>
        </React.Fragment>
      ))}
    </Box>
  );
};

/** When the prices were last pulled. Shown rather than hidden: the poller
 * runs daily, so a figure can legitimately be a day old, and a quiet stale
 * number is worse than a dated one. */
const PriceAsOf: React.FC<{ at: string | undefined }> = ({ at }) =>
  at ? (
    <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.66rem', mt: 1 }}>
      Jita 4-4, as of {new Date(`${at.replace(' ', 'T')}Z`).toLocaleString()}
    </Typography>
  ) : null;

/** One site's full reference detail — NPC composition, loot, hacking notes,
 * gas specifics — reached by clicking a site badge in SystemIntelPanel's
 * NPC Sites panel or SystemSites' category lists. Always nested under
 * /system/:slug/sites/:className/:siteName now (the standalone class-only
 * browser was removed as redundant). `?system=<id>` is kept as a fallback
 * data source only, in case this is ever mounted without the outlet
 * context again. */
const SiteDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { className = '', siteName = '' } = useParams();
  const [searchParams] = useSearchParams();
  // Prefer the parent route's already-fetched system (the outlet context)
  // over the query param, so the header and "context:" line can't disagree
  // on a hand-edited URL.
  const parentIntel = useOutletContext<SystemIntel | null>();
  const querySystemId = searchParams.get('system') ? Number(searchParams.get('system')) : undefined;
  const systemId = parentIntel?.system.solar_system_id ?? querySystemId;

  const { data, isLoading } = useQuery({
    queryKey: ['site-detail', className, siteName, systemId],
    queryFn: () => fetchSiteDetail(className, siteName, systemId),
  });

  const isRealClass = className !== 'gas' && className !== 'ore';
  const activity = className === 'gas' ? 'gas huffing' : className === 'ore' ? 'mining' : 'ratting';
  const recommendationClass = isRealClass ? className : (parentIntel?.system.system_class ?? '');
  const recommendations = useQuery({
    queryKey: ['fitting-recommendations', recommendationClass, activity, siteName],
    queryFn: () => fetchFittingRecommendations(recommendationClass, activity, siteName),
  });
  const recommendedFittings = recommendations.data ?? [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress sx={{ color: C.blue }} />
      </Box>
    );
  }

  const detail = data?.detail ?? null;
  const accent = (detail && CATEGORY_ACCENT[detail.category]) || C.cyan;
  const blueLoot = data?.blueLootPrices ?? [];

  /* The one money number for the "At a Glance" strip.
   *
   * Which number that is depends on what the site pays out, and the two are
   * not comparable: a harvest site's value is the field in front of you
   * (units × current Jita buy), while a combat site's is the wiki's flat
   * blue-loot figure — that one is a game constant per site, not a market
   * quote, so it is quoted as-is rather than recomputed. Sites with neither
   * simply have no block, which is honest; inventing a total for a relic
   * site whose payout is a random hacking table would not be. */
  const payout = (() => {
    const fieldValue = (detail?.resources ?? []).reduce((sum, r) => {
      const price = data?.resourcePrices?.[r.name];
      return sum + (r.units !== undefined && price?.buy ? r.units * price.buy : 0);
    }, 0);
    if (fieldValue > 0) {
      return {
        label: 'field value',
        value: isk(fieldValue),
        hint: `${nf.format(Math.round(fieldValue))} ISK for the whole field at current Jita buy — before hauling it out.`,
      };
    }
    if (detail?.blueloot) {
      // The catalogue stores this as the wiki writes it ("9,600,000 ISK"),
      // which is the transcription of record. Show the magnitude when it
      // parses cleanly, the original string when it doesn't — some entries
      // are ranges or prose, and mangling those would be worse than long.
      const parsed = Number(detail.blueloot.replace(/[^\d]/g, ''));
      const looksLikeOneNumber = /^[\d,]+\s*(ISK)?$/i.test(detail.blueloot.trim());
      return {
        label: 'blue loot',
        value: looksLikeOneNumber && parsed > 0 ? isk(parsed) : detail.blueloot,
        hint: `${detail.blueloot} in Sleeper components, per this site's wiki page. Component prices below.`,
      };
    }
    return null;
  })();

  return (
    <Stack spacing={3} sx={revealSx()}>
      <ScanFrame color={accent} corners={false} sx={{ ...igniteSx(accent), display: 'block' }}>
        <Typography
          sx={{ ...lcarsLabelSx(C.textDim, '0.8rem'), cursor: 'pointer', '&:hover': { color: C.text } }}
          onClick={() => navigate(-1)}
        >
          &larr; Back
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
          <Typography
            variant="h1"
            sx={{
              color: accent,
              fontFamily: FONT.lcars,
              fontWeight: 700,
              letterSpacing: '0.02em',
              fontSize: { xs: '2rem', md: '2.6rem' },
              lineHeight: 1,
              textShadow: `0 0 24px ${hexToRgba(accent, 0.35)}`,
            }}
          >
            {siteName}
          </Typography>
          {/* The category IS what this page is about — it earns the solid
              block; the class stays an outlined tag beside it. */}
          {detail && <LcarsBlock label={detail.category} accent={accent} size="sm" />}
          {className !== 'gas' && className !== 'ore' && <StatusBadge label={className} accent={classColor(className)} />}
        </Stack>
        <LcarsRule accent={accent} sx={{ mt: 1.25, maxWidth: 320 }} />
        {data?.system && (
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.8rem' }}>
            context: {data.system.name}
          </Typography>
        )}
      </ScanFrame>

      {!detail ? (
        <ConsolePanel title="Not Catalogued" accent={C.neutral} corners={false} sx={frameSx(C.neutral)}>
          <Typography sx={{ color: C.textDim }}>
            No detail curated yet for this site — combat/relic/data/gas/ore site detail is hand-compiled from real
            references (EVE University wiki, anoik.is, ellatha.com), same policy as the wormhole-static seed data.
          </Typography>
        </ConsolePanel>
      ) : (
        <>
        {/* The summary comes before the evidence. Everything in the grid
            below is the working behind these numbers. */}
        <SiteBriefing threat={data?.threat ?? null} accent={accent} damageTypes={detail.damageTypes} payout={payout} />

        {/* `minmax(0, 1fr)`, never a bare `1fr`: a bare fr track resolves its
            minimum to the widest child's min-content, so one long wave row or
            fit name silently widened the whole document — 91px past a 390px
            phone. The zero minimum is what lets these panels actually shrink
            and their own overflow rules take over. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
          <ConsolePanel title="NPC Composition" accent={accent} corners={false} sx={frameSx(accent)}>
            {/* The structured table when the composition parsed, the raw
                sentence when it didn't. Never a partial table: a wave missing
                from the list understates what is actually in the site. */}
            {data?.waves ? (
              <NpcWaveTable waves={data.waves} />
            ) : (
              <Typography sx={{ color: C.text, mb: 1 }}>{detail.npcComposition}</Typography>
            )}
            {detail.spawnTrigger && (
              <Typography sx={{ color: C.textDim, fontSize: '0.82rem', mt: 1.5, mb: 0.5 }}>
                {detail.spawnTrigger}
              </Typography>
            )}
            {/* The catalogue's blanket "all four types" badge row used to sit
                here. At a Glance now derives the REAL split from each hull's
                dogma and shows it as a proportional bar, so repeating the
                undifferentiated list underneath only argued with it. The
                fallback for a site with no derivable profile still lives in
                the briefing, which is the one place damage is described. */}
            {detail.tankNotes && (
              <Typography sx={{ color: C.textDim, fontSize: '0.82rem', mt: 1 }}>{detail.tankNotes}</Typography>
            )}
          </ConsolePanel>

          {/* Contents only. The guard wave is the NPC Composition panel's job
              and it does it properly — hull, orbit, EWAR, DPS and the spawn
              delay. A second "Guardians: 4x Sleepless Sentinel" line sat here
              repeating a worse version of that, and only on gas sites, so ore
              and ice looked like they had no guards at all.
              Placed right after NPC Composition, not after Loot: on a gas/
              ore/ice site this is the second-most-read box on the page, and
              a 2-col grid means "right after" is what puts it in the TOP row
              beside NPC rather than stranded on row two by itself. */}
          {HARVEST.has(detail.category) && detail.resources && detail.resources.length > 0 && (
            <ConsolePanel
              title={detail.category === 'gas' ? 'Gas Harvesting' : detail.category === 'ice' ? 'Ice Field' : 'Ore Field'}
              accent={C.cyan}
              corners={false}
              sx={frameSx(C.cyan)}
            >
              <ResourceTable rows={detail.resources} prices={data?.resourcePrices ?? {}} />
              <PriceAsOf at={Object.values(data?.resourcePrices ?? {})[0]?.updatedAt} />
            </ConsolePanel>
          )}

          {(detail.category === 'relic' || detail.category === 'data') && (detail.hackingDifficulty || (detail.canLootTable && detail.canLootTable.length > 0)) && (
            <ConsolePanel title="Hacking" accent={C.gold} corners={false} sx={frameSx(C.gold)}>
              {detail.hackingDifficulty && (
                <StatusBadge
                  label={`difficulty: ${detail.hackingDifficulty}`}
                  accent={detail.hackingDifficulty === 'high' ? C.red : detail.hackingDifficulty === 'medium' ? C.gold : C.cyan}
                />
              )}
              {detail.canLootTable && detail.canLootTable.length > 0 && (
                <Typography sx={{ color: C.text, fontSize: '0.85rem', mt: 1 }}>
                  Can loot: {detail.canLootTable.join(', ')}
                </Typography>
              )}
            </ConsolePanel>
          )}

          {/* The same panel as the system page's "Effect & Bonuses", because
              it is the same fact — gold accent, glowing display-face label,
              and the shared EffectStats table with its dotted leaders and
              staggered magnitude bars.

              This used to hand-roll its own rows: mono labels at dim alpha,
              no bars, no animation, and a blue accent found nowhere else on
              an effect. That made one system's effect look like two
              different kinds of data depending on which page you read it
              on, and it re-implemented a component that already existed.

              Below the top row rather than beside NPC: the effect modifies
              what the NPC Composition box already states, so it reads
              second, not as a competing headline. */}
          {data?.systemEffect && (
            <ConsolePanel
              title={`System Effect — ${data.system?.name}`}
              accent={C.gold}
              corners={false}
              sx={frameSx(C.gold)}
            >
              <Typography
                sx={{
                  fontFamily: FONT.display,
                  color: C.text,
                  fontWeight: 700,
                  fontSize: '1.15rem',
                  letterSpacing: '0.02em',
                  textShadow: glow(C.gold, 14, 0.3),
                  mb: 0.5,
                }}
              >
                {data.systemEffect.label}
              </Typography>
              <Typography sx={{ color: C.text, fontSize: '0.92rem', lineHeight: 1.55, mb: 1.5 }}>
                {data.systemEffect.summary}
              </Typography>
              <EffectStats stats={data.systemEffect.stats} />
            </ConsolePanel>
          )}

          {/* Always rendered for a catalogued site. This panel used to be
              conditional on there BEING a blue-loot figure, so a site
              without one simply had no line — indistinguishable from a site
              nobody had looked up, and the reason "Solar Cell has blue loot
              but Outpost Frontier Stronghold doesn't" looked like a fact
              about the sites rather than a gap in the data. It is a fact
              about ore and ice fields, and now it says so. */}
          <ConsolePanel title="Loot &amp; Escalation" accent={C.gold} corners={false} sx={frameSx(C.gold)}>
            <Stack spacing={1}>
                {detail.blueloot ? (
                  <Typography sx={{ color: C.text, fontSize: '0.85rem' }}>Blue loot: {detail.blueloot}</Typography>
                ) : (
                  <Typography sx={{ color: C.textDim, fontSize: '0.85rem' }}>
                    Blue loot: none. The wiki lists no Sleeper-component payout for this site &mdash; what it pays is
                    the field itself, plus whatever the guard wave drops.
                  </Typography>
                )}
                {detail.salvage && (
                  <Typography sx={{ color: C.text, fontSize: '0.85rem' }}>Salvage: {detail.salvage}</Typography>
                )}
                {detail.escalationChance && (
                  <Typography sx={{ color: C.text, fontSize: '0.85rem' }}>
                    Escalation: {detail.escalationChance}
                  </Typography>
                )}
                {detail.possibleSpawns && detail.possibleSpawns.length > 0 && (
                  <Typography sx={{ color: C.textDim, fontSize: '0.82rem' }}>
                    Possible spawns: {detail.possibleSpawns.join(', ')}
                  </Typography>
                )}
                {/* The wiki's blue-loot figure above says what the site pays;
                    this says what that pays TODAY. Both, because the flat
                    figure is stable reference and the price is not. */}
                {blueLoot.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <BlueLootPanel prices={blueLoot} />
                    <PriceAsOf at={blueLoot[0]?.updatedAt} />
                  </Box>
                )}
            </Stack>
          </ConsolePanel>

          {/* The fourth box, and the only one that answers back: every other
              panel here states what the site does to you, this one states
              what to bring. It belongs in the grid beside them rather than
              in a strip underneath, because comparing a fit's EHP against
              the threat block's peak alpha is the actual reason both
              numbers are on this page. */}
          <ConsolePanel title="Suggested Fittings" accent={C.blue} corners={false} sx={frameSx(C.blue)}>
            {recommendedFittings.length > 0 ? (
              <SuggestedFittings fittings={recommendedFittings} />
            ) : (
              <Stack spacing={0.75}>
                <Typography sx={{ color: C.textDim, fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Nothing tagged for this site yet.
                </Typography>
                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/fittings')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate('/fittings');
                    }
                  }}
                  sx={{
                    ...lcarsLabelSx(C.cyan, '0.66rem'),
                    cursor: 'pointer',
                    outline: 'none',
                    '&:hover': { color: C.textBright },
                    '&:focus-visible': { color: C.textBright, textDecoration: 'underline' },
                  }}
                >
                  Paste a fit &amp; tag it &rarr;
                </Typography>
              </Stack>
            )}
          </ConsolePanel>
        </Box>
        </>
      )}

      {/* The citation was already here as plain text. It is now the way OUT
          of this page: everything above is the operational read, and the
          wiki page it was transcribed from is the long-form version —
          room-by-room prose, screenshots, tactics discussion — that this
          console deliberately does not try to reproduce. */}
      {detail?.source && (
        <Typography
          component="a"
          href={detail.source}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: hexToRgba(C.neutral, 0.85),
            fontFamily: FONT.mono,
            fontSize: '0.68rem',
            textDecoration: 'none',
            transition: 'color 0.15s ease',
            '&:hover': { color: C.cyan },
          }}
        >
          full write-up on the source page ↗ {detail.source}
        </Typography>
      )}
    </Stack>
  );
};

export default SiteDetailPage;
